import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, X, Search } from 'lucide-react';
import { formatEuro } from '@/utils/format';
import type { StructureMatchCandidate } from '../hooks/findStructureMatch';
import type { DatabaseMaterial } from '@/hooks/useMaterials';

interface MissingStructureBannerProps {
  missing: 'structure_frame' | 'structure_guide';
  candidates: StructureMatchCandidate[];
  /** Inserisce il prodotto scelto accanto al profilo gia' presente. */
  onUse: (material: DatabaseMaterial) => void;
  /** Apre il picker filtrato sulla categoria mancante. */
  onBrowse: () => void;
  onDismiss: () => void;
}

/**
 * Avviso non bloccante: alla parete manca la guida (o il montante).
 *
 * Sostituisce l'inserimento automatico che c'era prima. Il sistema non decide
 * piu' da solo quale profilo abbinare — propone qualche candidato e lascia
 * scegliere, perche' l'abbinamento per sola larghezza sbagliava troppo spesso.
 *
 * Struttura ripresa da `CertifiedMatchBanner`, in ambra invece che blu perche'
 * qui si segnala qualcosa che manca, non un'opportunita'.
 */
const MissingStructureBanner: React.FC<MissingStructureBannerProps> = ({
  missing,
  candidates,
  onUse,
  onBrowse,
  onDismiss,
}) => {
  const isGuide = missing === 'structure_guide';
  const what = isGuide ? 'la guida' : 'il montante';

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-amber-900 mb-0.5">
          Manca {what}
        </div>
        <div className="text-xs text-amber-800/80">
          {isGuide
            ? 'Una parete con orditura metallica va chiusa con le guide a pavimento e a soffitto.'
            : 'Ci sono le guide ma nessun montante: la struttura non è completa.'}
        </div>

        {candidates.length > 0 && (
          <div className="mt-2 space-y-1">
            <div className="text-[10px] uppercase tracking-wide text-amber-700/70 font-medium">
              Possibili abbinamenti — verifica prima di usarli
            </div>
            {candidates.map(({ material, differentSupplier }) => (
              <div
                key={material.id}
                className="flex items-center gap-2 rounded border border-amber-200 bg-white px-2 py-1.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-zinc-800 truncate">
                    {material.name}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                    <span>{material.supplier}</span>
                    {material.sheet_thickness != null && <span>· lamierino {material.sheet_thickness} mm</span>}
                    {material.length != null && <span>· barra {material.length} mm</span>}
                    <span>· {formatEuro(material.unit_price)}/{material.unit}</span>
                  </div>
                </div>
                {differentSupplier && (
                  <Badge
                    variant="outline"
                    className="text-[9px] border-amber-300 text-amber-700 bg-amber-50 flex-shrink-0"
                  >
                    altro fornitore
                  </Badge>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[11px] px-2 flex-shrink-0"
                  onClick={() => onUse(material)}
                >
                  Usa questa
                </Button>
              </div>
            ))}
          </div>
        )}

        <Button
          size="sm"
          onClick={onBrowse}
          className="gap-1.5 h-7 mt-2"
        >
          <Search className="h-3 w-3" />
          Scegli dal catalogo
        </Button>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Chiudi avviso"
        className="flex-shrink-0 text-amber-700/60 hover:text-amber-900 p-0.5"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default MissingStructureBanner;
